/**
 * Utilities for parsing and processing AI responses
 */

export interface MetricInsight {
  label: string
  value: string
  context: string
  type: 'volatility' | 'average' | 'peak' | 'summary'
}

export interface ParsedPatternResponse {
  metrics: MetricInsight[]
  fullAnalysis: string
  hasTldr: boolean
}

/**
 * Parse AI pattern analysis response to extract structured metrics and full analysis
 *
 * Expected format from AI (unified format with separator for all metrics):
 * TLDR:
 * VOLATILITA: [value] - [context]
 * PRŮMĚR: [value] - [context]
 * PEAK: [value] - [context]
 * SHRNUTÍ: [value] - [context]
 *
 * [Full analysis text...]
 *
 * Note: Backward compatible with old SHRNUTÍ format without separator
 *
 * @param response - The complete AI response text
 * @returns Parsed response with structured metrics and full analysis
 */
export function parsePatternResponse(response: string): ParsedPatternResponse {
  // Check if response contains TLDR section - more flexible regex
  const tldrMatch = response.match(/TLDR:\s*\n([\s\S]*?)(?:\n\n|$)/i)

  if (!tldrMatch) {
    // No TL;DR found, return entire response as full analysis
    return {
      metrics: [],
      fullAnalysis: response.trim(),
      hasTldr: false,
    }
  }

  // Extract metrics from TL;DR section
  const tldrSection = tldrMatch[1]
  const metrics: MetricInsight[] = []

  // Parse VOLATILITA line - search in full response for better reliability
  const volatilityMatch = response.match(/VOLATILITA:\s*([^-\n]+?)\s*-\s*([^\n]+)/i)
  if (volatilityMatch) {
    metrics.push({
      label: 'Volatilita',
      value: volatilityMatch[1].trim(),
      context: volatilityMatch[2].trim(),
      type: 'volatility',
    })
  }

  // Parse PRŮMĚR line
  const averageMatch = response.match(/PRŮMĚR:\s*([^-\n]+?)\s*-\s*([^\n]+)/i)
  if (averageMatch) {
    metrics.push({
      label: 'Průměr',
      value: averageMatch[1].trim(),
      context: averageMatch[2].trim(),
      type: 'average',
    })
  }

  // Parse PEAK line
  const peakMatch = response.match(/PEAK:\s*([^-\n]+?)\s*-\s*([^\n]+)/i)
  if (peakMatch) {
    metrics.push({
      label: 'Peak',
      value: peakMatch[1].trim(),
      context: peakMatch[2].trim(),
      type: 'peak',
    })
  }

  // Parse SHRNUTÍ line (now with dash separator, with backward compatibility)
  // Try with separator first (new format)
  const summaryMatchWithSeparator = response.match(/SHRNUTÍ:\s*([^-\n]+?)\s*-\s*([^\n]+)/i)
  if (summaryMatchWithSeparator) {
    metrics.push({
      label: 'Shrnutí',
      value: summaryMatchWithSeparator[1].trim(),
      context: summaryMatchWithSeparator[2].trim(),
      type: 'summary',
    })
  } else {
    // Fallback to old format without separator (backward compatibility)
    const summaryMatch = response.match(/SHRNUTÍ:\s*([^\n]+)/i)
    if (summaryMatch) {
      metrics.push({
        label: 'Shrnutí',
        value: summaryMatch[1].trim(),
        context: '',
        type: 'summary',
      })
    }
  }

  // Extract full analysis (everything after TL;DR section)
  const tldrEndIndex = response.indexOf(tldrMatch[0]) + tldrMatch[0].length
  const fullAnalysis = response
    .substring(tldrEndIndex)
    .trim()

  return {
    metrics,
    fullAnalysis,
    hasTldr: metrics.length > 0,
  }
}

/**
 * Validate that metrics meet quality standards
 * - Should have exactly 4 metrics (volatility, average, peak, summary)
 * - Each metric should have required fields
 *
 * @param metrics - Array of metric insights
 * @returns true if metrics are valid, false otherwise
 */
export function validateMetrics(metrics: MetricInsight[]): boolean {
  if (metrics.length !== 4) {
    return false
  }

  const types = new Set(metrics.map(m => m.type))
  const requiredTypes: Array<MetricInsight['type']> = ['volatility', 'average', 'peak', 'summary']

  return requiredTypes.every(type => types.has(type))
}
